// ChromeShellFrame — the ONE shell (F45), assembling the chrome
// (F31/F35/F40/F5 reconcile, sketches/shell-chrome/ShellChrome.tsx) against
// the REAL AppShellModel.
//
// Reuses AppShell (the three-zone layout primitive) so the dock's
// geometry/resize/drag-to-open behaviour comes for free — the `nav` slot seats
// the Sidebar (SidebarHost). `material` (Centre) renders TABLESS content
// whenever the pane tree isn't split (F5 — no ResourceTabs strip mounts for
// the single-leaf case).
//
// F58 — the nav ZONE width is driven by `frame.nav` (the shared rail state),
// un-pinning the former F47 constant. The rail FILLS its zone (width:100%), so
// expanded (240px) pushes the centre and collapsed (48px) is the icon column —
// no overflow-past-the-zone overlay, so the empty-column bug F47 guarded
// against cannot occur (the zone and the rail are the same width by
// construction). The chevron at the top of the rail dispatches `toggle-rail`.
//
// F58 — the former IAM top strip (ChromeTopRow) is GONE: its workspace-name
// display and account affordance folded into the rail's footer, so the chrome
// shell is the three-zone grid with no top band. `topStrip` is simply omitted.
//
// Redesign step 1 — the right zone is the docked chat panel (SecondaryDock →
// ChatPanelHost), a floating card; the centre is the main view (the Library by
// default). No dock ribbon: the panel's own head switches its views, and while
// it is closed the minimised chat (AssistantPill, pen K-08) docks bottom-right:
// it says what the assistant is doing and brings the panel back. Over the
// Studio canvas it is the shell's compact pill (pen "Projects and Studio",
// decision 1).

export { ChromeShellFrame };

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { AppShell } from "../../../components/AppShell/AppShell.js";
import { AssistantPill } from "../../../components/AssistantPill/AssistantPill.js";
import { PanelOpenHandle } from "../../../components/PanelOpenHandle/PanelOpenHandle.js";
import { PanelResizeHandle } from "../../../components/PanelResizeHandle/PanelResizeHandle.js";
import { TooltipProvider } from "../../../components/Tooltip/Tooltip.js";
import { assistantPillState } from "../../assistantPill.js";
import { chatPlaceOf, cornerInset } from "../../frame.js";
import { panelDragWidth } from "../../panelDragOpen.js";
import { DOCK } from "../../panelLayout.js";
import { Centre } from "../Centre.js";
import { useAppShell } from "../context.js";
import { SecondaryDock } from "../SecondaryDock.js";
import "./ChromeShellFrame.css";
import { helpInOutline, studioOpenOf } from "../Centre/studioOpen.js";
import { HelpEntry } from "./FeedbackHost.js";
import { SidebarHost } from "./SidebarHost.js";

const ChromeShellFrame = (): ReactNode => {
    const { t } = useLingui();
    const {
        frame,
        dispatch,
        dock,
        surface,
        panelLayout,
        resizeDock,
        resetDock,
        setDockDragPull,
        endDockDrag,
        dockDragPull,
        dockDragCfg,
        closeSecondary,
        chatState,
        sessionFor,
        activeConversationId,
        tabs,
        library,
    } = useAppShell();
    const insetInlineEnd = cornerInset(
        dock,
        panelLayout.dockWidth,
        chatPlaceOf(tabs.active, frame.studioSurface),
    );

    return (
        // ONE TooltipProvider covers the WHOLE chrome shell — rail, centre AND
        // docks — the Radix single-provider pattern Tooltip.tsx describes. Any
        // Tooltip anywhere in the frame (e.g. F75's SummaryAiMark on the
        // Journal summary heading, rendered in the centre) resolves to this
        // ancestor; previously only the rail was wrapped, so a centre/dock
        // tooltip crashed with "Tooltip must be used within TooltipProvider".
        <TooltipProvider>
            <AppShell
                // F46/F58 scoping hook — see ChromeRail.css's
                // `.bilda-app-shell--chrome` rules (chrome-only nav-zone
                // overrides).
                className="bilda-app-shell--chrome"
                dock={dock}
                // F58 — the SHARED rail state, un-pinned from the former F47
                // constant: the top chevron dispatches `toggle-rail`.
                // The menu pushes the page at every width, the Studio
                // included; it never lies over it (owner, 30 Oct).
                navState={frame.nav}
                surface={surface}
                nav={<SidebarHost />}
                toolbar={null}
                material={<Centre />}
                secondary={<SecondaryDock />}
                railWidth={panelLayout.railWidth}
                dockWidth={panelLayout.dockWidth}
                dockSash={
                    <PanelResizeHandle
                        label={t`Resize panel`}
                        value={panelLayout.dockWidth}
                        min={DOCK.min}
                        max={DOCK.max}
                        growSign={-1}
                        onResizeBy={resizeDock}
                        onReset={resetDock}
                    />
                }
                dockOpenHandle={
                    <PanelOpenHandle
                        label={t`Drag or click to open the assistant`}
                        growSign={-1}
                        onDragMove={setDockDragPull}
                        onDragEnd={endDockDrag}
                        onOpen={() =>
                            dispatch({
                                type: "summon-slot",
                                slot: "assistant",
                            })
                        }
                        onDragCancel={() => setDockDragPull(null)}
                    />
                }
                dockDragWidth={
                    dockDragPull === null
                        ? undefined
                        : panelDragWidth(dockDragPull, dockDragCfg)
                }
                onSecondaryScrimClick={closeSecondary}
            />
            {/* The bottom-right corner (pen Q68): the minimised assistant,
                then the Help tab on the page's bottom edge. Left of whatever
                panel holds the right edge (the open assistant, or the
                Studio's action panel), so it never lies over a composer's
                Send. */}
            <div className="bilda-shell-corner" style={{ insetInlineEnd }}>
                {dock.type === "collapsed" ? (
                    <AssistantPill
                        state={assistantPillState(
                            sessionFor(activeConversationId),
                            chatState.liveness,
                            activeConversationId,
                        )}
                        compact={tabs.active.type === "studio"}
                        onOpen={() =>
                            dispatch({ type: "summon-slot", slot: "assistant" })
                        }
                    />
                ) : null}
                {/* In the Studio's pages Help sits at the outline's foot
                    (pen Q76), so it never lies on the page. */}
                {tabs.active.type === "studio" &&
                helpInOutline(
                    studioOpenOf(
                        frame.studioProject,
                        library === null ? null : (library.projects ?? []),
                    ),
                    frame.studioSurface,
                ) ? null : (
                    <HelpEntry look="tab" />
                )}
            </div>
        </TooltipProvider>
    );
};
